import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { UserSettings } from 'src/Libs/user/types'
import { useUserSettings } from 'src/Hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { BannerKey } from '../types'
import { useBannerContext } from '../provider'

interface SaveBannerProps {
  userId?: number
  userSettings?: UserSettings
  removedBanners?: number[]
}

interface ReturnType {
  hideBannerPermanently: (bannerKey: BannerKey) => Promise<void>
}

/**
 * This hook provides logic to hide a banner on server side
 */
const useHideBanner = (successMessageKey?: string): ReturnType => {
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()
  const { removeBanner } = useBannerContext()

  const { userSettings, updateUserSettings } = useUserSettings()

  const userId = useTypedSelector(state => state.auth.userId)

  const storeHiddenBannersInUserSettings = async ({ userId, removedBanners }: SaveBannerProps): Promise<void> => {
    if (userId) {
      try {
        await updateUserSettings({ removedBanners })
        if (successMessageKey) enqueueSuccessStack(t(successMessageKey))
      } catch (err) {
        enqueueErrorStack(`${t('installation-extension.banner-disabled.failed')}: ${err}`)
        throw err
      }
    }
  }

  const hideBannerPermanently = async (bannerKey: BannerKey): Promise<void> => {
    const isBannerAlreadyHidden = Boolean(userSettings.removedBanners?.includes(bannerKey))
    if (isBannerAlreadyHidden) return

    const removedBanners = [...(userSettings.removedBanners ?? []), bannerKey]
    await storeHiddenBannersInUserSettings({ userId, removedBanners })
    removeBanner(bannerKey)
  }

  return { hideBannerPermanently }
}

export default useHideBanner
